import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { COMPANY_INFO } from "../../../mocks/aegis-data";

interface HeroProps {
  headline: string;
  subheadline: string;
  badge?: string;
  ctaPrimary: { text: string; href: string };
  ctaSecondary?: { text: string; href: string };
  breadcrumb?: string[];
  backgroundImage?: string;
}

export default function HeroSection({
  headline,
  subheadline,
  badge,
  ctaPrimary,
  ctaSecondary,
  breadcrumb,
  backgroundImage,
}: HeroProps) {
  const bg = backgroundImage ?? COMPANY_INFO.heroBg;
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative min-h-screen flex overflow-hidden">
      {/* Right image panel — 45% */}
      <div className="absolute inset-y-0 right-0 w-full lg:w-1/2 z-0">
        <img
          src={bg}
          alt="Aegis Construction Services — Custom Home Builder Sarasota"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        {/* Directional gradient covering right panel toward center */}
        <div className="absolute inset-0 bg-gradient-to-l from-transparent via-aegisDark/40 to-aegisDark/95 lg:to-aegisDark" />
        <div className="absolute inset-0 bg-gradient-to-t from-aegisDark/70 via-transparent to-aegisDark/30" />
      </div>

      {/* Left dark content panel */}
      <div className="absolute inset-y-0 left-0 w-full lg:w-1/2 bg-aegisDark z-0 hidden lg:block" />

      {/* Gold left edge accent */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-aegisGold z-20 hidden lg:block" />

      {/* Content layer */}
      <div className="relative z-10 w-full flex items-center min-h-screen">
        <div className="w-full max-w-7xl mx-auto px-8 lg:px-12 pt-28 pb-20">
          <div className="lg:max-w-[54%]">

            {/* Breadcrumb */}
            {breadcrumb && breadcrumb.length > 0 && (
              <div
                className="flex items-center gap-2 mb-8 transition-all duration-700"
                style={{ opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(12px)" }}
              >
                {breadcrumb.map((crumb, i) => (
                  <span key={i} className="flex items-center gap-2">
                    {i > 0 && <span className="text-white/20 text-xs">›</span>}
                    <span className={`font-inter text-xs tracking-widest uppercase ${i === breadcrumb.length - 1 ? "text-aegisGold" : "text-white/35"}`}>
                      {crumb}
                    </span>
                  </span>
                ))}
              </div>
            )}

            {/* Badge line */}
            {badge && (
              <div
                className="flex items-center gap-4 mb-8 transition-all duration-700 delay-75"
                style={{ opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(12px)" }}
              >
                <div className="h-px w-10 bg-aegisGold" />
                <span className="font-inter text-xs tracking-[0.2em] uppercase text-aegisGold">{badge}</span>
              </div>
            )}

            {/* Headline */}
            <h1
              className="font-cormorant font-semibold text-white leading-[1.05] mb-8 transition-all duration-700 delay-100"
              style={{
                fontSize: "clamp(3rem, 5.5vw, 5.5rem)",
                opacity: loaded ? 1 : 0,
                transform: loaded ? "translateY(0)" : "translateY(20px)",
              }}
            >
              {headline}
            </h1>

            {/* Gold rule */}
            <div
              className="flex items-center gap-3 mb-8 transition-all duration-700 delay-150"
              style={{ opacity: loaded ? 1 : 0 }}
            >
              <div className="w-16 h-0.5 bg-aegisGold" />
              <div className="w-2 h-2 border border-aegisGold rotate-45 flex-shrink-0" />
              <div className="w-6 h-0.5 bg-aegisGold/30" />
            </div>

            {/* Subheadline */}
            <p
              className="font-inter text-white/65 text-lg leading-[1.75] max-w-xl mb-12 transition-all duration-700 delay-200"
              style={{ opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(14px)" }}
            >
              {subheadline}
            </p>

            {/* CTAs */}
            <div
              className="flex flex-col sm:flex-row gap-4 mb-14 transition-all duration-700 delay-300"
              style={{ opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(14px)" }}
            >
              <Link
                to={ctaPrimary.href}
                className="inline-flex items-center justify-center gap-3 bg-aegisGold text-white font-inter font-semibold text-sm tracking-[0.15em] uppercase px-10 py-4.5 whitespace-nowrap cursor-pointer hover:bg-amber-700 transition-all duration-300 group"
                style={{ paddingTop: "1.125rem", paddingBottom: "1.125rem" }}
              >
                {ctaPrimary.text}
                <i className="ri-arrow-right-long-line text-base group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
              {ctaSecondary && (
                <a
                  href={COMPANY_INFO.phoneHref}
                  className="inline-flex items-center justify-center gap-3 border border-white/30 text-white font-inter font-medium text-sm tracking-[0.15em] uppercase px-10 whitespace-nowrap cursor-pointer hover:border-aegisGold hover:text-aegisGold transition-all duration-300"
                  style={{ paddingTop: "1.125rem", paddingBottom: "1.125rem" }}
                >
                  <i className="ri-phone-line" />
                  {ctaSecondary.text}
                </a>
              )}
            </div>

            {/* Trust row */}
            <div
              className="flex flex-wrap items-center gap-x-7 gap-y-3 pt-8 border-t border-white/10 transition-all duration-700 delay-500"
              style={{ opacity: loaded ? 1 : 0 }}
            >
              {[
                { icon: "ri-shield-check-line", text: "CGC1524141 Licensed & Insured" },
                { icon: "ri-calendar-check-line", text: "Since 2011 in Sarasota" },
                { icon: "ri-team-line", text: "In-House Crews Only" },
              ].map((t) => (
                <div key={t.text} className="flex items-center gap-2.5">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className={`${t.icon} text-aegisGold text-sm`} />
                  </div>
                  <span className="font-inter text-xs text-white/45 tracking-wide">{t.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gradient bleed into next section */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-aegisDark to-transparent z-10" />
    </section>
  );
}
